import { createAsyncThunk } from '@reduxjs/toolkit';

import store from 'store/store';

import instance from 'utils/API';

import { HistoryActionTypes, TicketStages } from 'core/enums/common';

import { mergeRelatedUserStoriesInUseCases, transformTestsResponseToState } from './helpers';
import {
	appendTickets,
	setIsTestCasesLoading,
	setTicketHistory,
	setTickets,
	updateLocalTicket,
} from './ticketSlice';
import {
	ISummaryStory,
	ISummaryStoryWithId,
	ITicketChangesHistoryResponse,
	IUsecaseData,
	IUsecases,
	LoadUseCasesResponse,
	TicketType,
} from './types';

export const getTicketsByUserId = createAsyncThunk(
	'tickets/getByUserId',
	async (
		{
			userId,
			currentPageNumber,
			recordsPerPage,
		}: { userId: string; currentPageNumber?: number; recordsPerPage?: number },
		{ dispatch, rejectWithValue }
	) => {
		try {
			const response = await instance.post(`/Tickets/tickets/${userId}`, {
				currentPageNumber: currentPageNumber ?? 1,
				recordsPerPage: recordsPerPage ?? 8,
			});

			const { data } = response.data;

			if (currentPageNumber) {
				dispatch(appendTickets(data));
			} else {
				dispatch(setTickets(data));
			}

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const createTicket = createAsyncThunk(
	'tickets/createTicket',
	async (
		{ userId, ticket }: { userId: string; ticket: Partial<TicketType> },
		{ rejectWithValue }
	) => {
		try {
			const response = await instance.post(`/Tickets/${userId}`, ticket);

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const updateTicket = createAsyncThunk(
	'tickets/updateTicket',
	async (data: TicketType, { rejectWithValue }) => {
		try {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { id, userStoryTests, useCases, ticketSummaries, ...ticketData } = data;

			const response = await instance.put(`/Tickets/${id}`, {
				...ticketData,
			});

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const deleteTicket = createAsyncThunk(
	'tickets/deleteTicket',
	async (ticketId, { rejectWithValue }) => {
		try {
			const response = await instance.delete(`/Tickets/${ticketId}`);
			return response.data;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const searchTickets = createAsyncThunk(
	'tickets/searchTickets',
	async (
		{
			userId,
			ticketTitle,
			currentPageNumber,
		}: { userId: string; ticketTitle: string; currentPageNumber?: number },
		{ rejectWithValue, dispatch }
	) => {
		try {
			const response = await instance.post(`/Tickets/search/${userId}`, {
				ticketTitle,
				pagination: {
					currentPageNumber: currentPageNumber ?? 1,
					recordsPerPage: 8,
				},
			});

			const { data } = response.data;

			if (currentPageNumber) {
				dispatch(appendTickets(data));
			} else {
				dispatch(setTickets(data));
			}

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const saveUserStories = createAsyncThunk(
	'userStories/saveUserStories',
	async (
		{ userStoriesData }: { userStoriesData: Array<ISummaryStoryWithId> },
		{ rejectWithValue }
	) => {
		try {
			const response = await instance.post('/TicketSummary/createTicketSummaries', userStoriesData);

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const editUserStories = createAsyncThunk(
	'userStories/editUserStories',
	async ({ userStoriesData }: { userStoriesData: Array<ISummaryStory> }, { rejectWithValue }) => {
		try {
			const response = await instance.put(`/TicketSummary/updateTicketSummaries`, userStoriesData);

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error?.response?.data);
		}
	}
);

export const deleteUserStories = createAsyncThunk(
	'userStories/deleteUserStories',
	async (
		{ storiesIds, ticketId }: { storiesIds: Array<string>; ticketId: string },
		{ rejectWithValue }
	) => {
		try {
			const response = await instance.delete(`/TicketSummary/deleteTicketSummariesByIds`, {
				data: storiesIds,
				params: {
					ticketId,
				},
			});

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error?.response?.data);
		}
	}
);

export const deleteUserStoriesScenarios = createAsyncThunk(
	'userStories/deleteUserStoriesScenarios',
	async (
		{ scenariosIds, ticketId }: { scenariosIds: Array<string>; ticketId: string },
		{ rejectWithValue }
	) => {
		try {
			const response = await instance.delete(`/TicketSummary/deleteTicketSummaryScenarios`, {
				data: scenariosIds,
				params: {
					ticketId,
				},
			});

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error?.response?.data);
		}
	}
);

export const deleteUserStoriesCriteria = createAsyncThunk(
	'userStories/deleteUserStoriesCriteria',
	async (
		{ criteriaIds, ticketId }: { criteriaIds: Array<string>; ticketId: string },
		{ rejectWithValue }
	) => {
		try {
			const response = await instance.delete(
				`/TicketSummary/deleteTicketSummaryAcceptanceCriteria`,
				{
					data: criteriaIds,
					params: {
						ticketId,
					},
				}
			);

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error?.response?.data);
		}
	}
);

export const loadUserStories = createAsyncThunk(
	'userStories/getUserStories',
	async ({ ticketId }: { ticketId: string }, { dispatch, rejectWithValue }) => {
		try {
			const response = await instance.get(`/TicketSummary/getTicketSummariesByTicketId`, {
				params: {
					ticketId,
				},
			});

			dispatch(
				updateLocalTicket({
					id: ticketId,
					ticketSummaries: response.data,
				})
			);

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const saveUseCases = createAsyncThunk(
	'usecases/saveUseCases',
	async ({ useCasesData }: { useCasesData: IUsecases[] }, { rejectWithValue }) => {
		try {
			const { data } = await instance.post('/Usecases/create', useCasesData);

			const currentTicketSummaries = store.getState().ticketRedux.activeTicket?.ticketSummaries;

			return mergeRelatedUserStoriesInUseCases(data.usecases, currentTicketSummaries);
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const updateUseCases = createAsyncThunk(
	'usecases/updateUseCases',
	async (
		{ useCaseData, useCaseId }: { useCaseData: IUsecaseData; useCaseId: string },
		{ rejectWithValue }
	) => {
		try {
			const response = await instance.put(`/Usecases/update?usecaseId=${useCaseId}`, useCaseData);

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const loadUseCases = createAsyncThunk(
	'usecases/getUseCases',
	async ({ ticketId }: { ticketId: string }, { dispatch, rejectWithValue }) => {
		try {
			const response = await instance.post<LoadUseCasesResponse>(`/Usecases/get`, {
				ticketId,
				paginationRequest: {
					currentPageNumber: 1,
					recordsPerPage: 20,
				},
			});

			const { data } = response.data;

			const currentTicketSummaries = store.getState().ticketRedux.activeTicket?.ticketSummaries;

			const dataWithSummaries = mergeRelatedUserStoriesInUseCases(data, currentTicketSummaries);

			dispatch(
				updateLocalTicket({
					id: ticketId,
					useCases: dataWithSummaries,
				})
			);

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const deleteTicketUsecases = createAsyncThunk(
	'usecases/deleteTicketUseCases',
	async ({ ticketId }: { ticketId: string }, { dispatch, rejectWithValue }) => {
		try {
			const response = await instance.delete(`/Usecases/delete?ticketId=${ticketId}`);

			dispatch(
				updateLocalTicket({
					id: ticketId,
					useCases: [],
				})
			);

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const createUserStoryTests = createAsyncThunk(
	'userStoryTest/createUserStoryTests',
	async ({ userStoryTests }: { userStoryTests: any }, { rejectWithValue }) => {
		try {
			const { data } = await instance.post(`/UserStoryTest/createUserStoryTests`, userStoryTests);

			return transformTestsResponseToState(data);
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const loadUserStoryTest = createAsyncThunk(
	'userStoryTest/getUserStoryTests',
	async ({ ticketId }: { ticketId: string }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(setIsTestCasesLoading(true));

			const { data } = await instance.get(`/UserStoryTest/getUserStoryTests`, {
				params: {
					ticketId,
				},
			});

			const userStoryTests = transformTestsResponseToState(data);

			dispatch(
				updateLocalTicket({
					id: ticketId,
					userStoryTests,
				})
			);

			dispatch(setIsTestCasesLoading(false));

			return userStoryTests;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const updateUserStoryTests = createAsyncThunk(
	'userStoryTest/updateUserStoryTests',
	async ({ testCase }: { testCase: any }, { rejectWithValue }) => {
		try {
			const { data } = await instance.put(`/UserStoryTest/updateUserStoryTests`, testCase);

			return data;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const deleteTestCases = createAsyncThunk(
	'userStoryTest/deleteTestCases',
	async (testCasesIds: Array<string>, { rejectWithValue }) => {
		try {
			const { data } = await instance.delete(`/UserStoryTest/deleteTestCases`, {
				data: testCasesIds,
			});

			return data;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const generateDoc = createAsyncThunk(
	'ticket/generateDoc',
	async (reportBase64: string[], { rejectWithValue }) => {
		try {
			const response = await instance.post(`/Tickets/generateDoc`, reportBase64, {
				responseType: 'blob',
			});

			const blob = new Blob([response.data], { type: 'application/octet-stream' });
			return URL.createObjectURL(blob);
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const addChangesHistory = createAsyncThunk(
	'ticket/addChangeHistory',
	async (
		{
			userId,
			ticketId,
			ticketStep,
			actionType,
			oldValue,
			newValue,
		}: {
			userId: string;
			ticketId: string;
			ticketStep: TicketStages;
			actionType: HistoryActionTypes;
			oldValue?: any;
			newValue?: any;
		},
		{ rejectWithValue }
	) => {
		try {
			const storedUser = localStorage.getItem('user');
			const user = storedUser ? JSON.parse(storedUser) : null;
			const userEmail = user ? user.email : '';
			const userFirstName = user ? user.firstName : '';
			const userLastName = user ? user.lastName : '';

			const userData = JSON.stringify({
				id: userId,
				email: userEmail,
				firstName: userFirstName,
				lastName: userLastName,
			});

			const payload = {
				ticketId,
				actionHistoryEnum: actionType,
				ticketCurrentStepEnum: ticketStep,
				userEmail: userData,
			};

			if (oldValue) Object.assign(payload, { oldValue });
			if (newValue) Object.assign(payload, { newValue });

			const response = await instance.post('ActionHistory/createActionHistory', payload);

			store.dispatch(getChangesHistory({ ticketId, ticketStep: TicketStages['general'] }));

			return response.data;
		} catch (error: any) {
			return rejectWithValue(error.response.data);
		}
	}
);

export const getChangesHistory = createAsyncThunk(
	'ticket/getChangeHistory',
	async (
		{
			ticketId,
			ticketStep,
		}: {
			ticketId: string;
			ticketStep: TicketStages;
		},
		{ dispatch }
	): Promise<ITicketChangesHistoryResponse> => {
		const response = await instance.post(`/ActionHistory/getActionHistory?TicketId=${ticketId}`, {
			ticketCurrentStepEnum: ticketStep,
			pagination: {
				currentPageNumber: 1,
				recordsPerPage: 16,
			},
		});

		if (store.getState().ticketRedux.activeTicket?.id === ticketId)
			dispatch(setTicketHistory(response.data.data));

		return response.data;
	}
);
